/* ============================================================
   HAPPY CLUCK - premium interactions
   Vanilla JS. No external deps. Performance-first:
   IntersectionObserver, passive listeners, rAF, transform/opacity only.
   ============================================================ */
(function () {
  "use strict";
  var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var money = function (cents) {
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency: window.Shopify && Shopify.currency ? Shopify.currency.active : "USD" }).format(cents / 100);
    } catch (e) { return "$" + (cents / 100).toFixed(2); }
  };

  /* ---------- Scroll reveals ---------- */
  function initReveals() {
    var els = Array.prototype.slice.call(document.querySelectorAll(".hc-reveal:not(.is-in)"));
    if (!els.length) return;
    if (reduce || !("IntersectionObserver" in window)) {
      els.forEach(function (el) { el.classList.add("is-in"); });
      return;
    }
    var pending = els.slice();
    function markIn(el) {
      el.classList.add("is-in");
      var i = pending.indexOf(el);
      if (i > -1) pending.splice(i, 1);
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { markIn(e.target); io.unobserve(e.target); }
      });
    }, { threshold: 0.14, rootMargin: "0px 0px -8% 0px" });
    els.forEach(function (el) { io.observe(el); });
    /* safety net: fast flick-scrolls can skip the IO callback entirely,
       leaving content stuck at opacity:0. Sweep on scroll (rAF-throttled)
       and reveal anything already at/above the trigger line. */
    var ticking = false;
    function sweep() {
      ticking = false;
      if (!pending.length) return;
      var vh = window.innerHeight || document.documentElement.clientHeight || 0;
      pending.slice().forEach(function (el) {
        if (el.getBoundingClientRect().top < vh * 0.92) { io.unobserve(el); markIn(el); }
      });
    }
    window.addEventListener("scroll", function () {
      if (!ticking) { ticking = true; window.requestAnimationFrame(sweep); }
    }, { passive: true });
  }

  /* ---------- Hero parallax (subtle, rAF) ---------- */
  function initParallax() {
    if (reduce) return;
    var layers = document.querySelectorAll("[data-hc-parallax]");
    if (!layers.length) return;
    var ticking = false;
    function update() {
      var y = window.pageYOffset;
      layers.forEach(function (l) {
        var speed = parseFloat(l.getAttribute("data-hc-parallax")) || 0.15;
        l.style.transform = "translate3d(0," + (y * speed).toFixed(1) + "px,0) scale(1.08)";
      });
      ticking = false;
    }
    window.addEventListener("scroll", function () {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ---------- Lazy hero / bg video ---------- */
  function initVideo() {
    var vids = document.querySelectorAll("video[data-hc-lazy]");
    if (!vids.length) return;
    var load = function (v) {
      var src = v.getAttribute("data-src");
      if (src && !v.src) { v.src = src; }
      v.querySelectorAll("source[data-src]").forEach(function (s) { s.src = s.getAttribute("data-src"); });
      v.load();
      var p = v.play(); if (p && p.catch) p.catch(function () {});
    };
    if (!("IntersectionObserver" in window)) { vids.forEach(load); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) { if (e.isIntersecting) { load(e.target); io.unobserve(e.target); } });
    }, { rootMargin: "200px" });
    vids.forEach(function (v) { io.observe(v); });
  }

  /* ---------- Marquee seamless loop ---------- */
  function initMarquee() {
    document.querySelectorAll(".hc-marquee__track").forEach(function (t) {
      if (t.dataset.cloned) return;
      t.dataset.cloned = "1";
      t.innerHTML = t.innerHTML + t.innerHTML;
    });
  }

  /* ---------- Cart config (live product/variant/selling-plan data from header) ---------- */
  var CartCfg = (function () {
    try { return JSON.parse(document.getElementById("hc-cart-config").textContent); }
    catch (e) { return { products: {} }; }
  })();
  /* Single source of truth for shipping/GWP economics (hc-cart-config JSON in hc-header.liquid) */
  var CFG = {
    threshold: Math.round((parseFloat(CartCfg.freeShipThreshold) || 70) * 100),
    shipFee: Math.round((parseFloat(CartCfg.shipFee) || 8) * 100),
    gwpThreshold: Math.round((parseFloat(CartCfg.gwpThreshold) || 85) * 100),
    gwpVariant: parseInt(CartCfg.gwpVariant, 10) || 0,
    subPct: parseFloat(CartCfg.subDiscountPct) || 20
  };
  var moneyTrim = function (cents) { return money(cents).replace(/\.00(\D|$)/, "$1"); };

  /* ---------- Cart drawer + /cart page (shared state machine) ----------
     The same render pipeline drives the slide-out drawer AND any
     [data-hc-cart-root] container (branded /cart page). Zones per root:
     [data-hc-cart-title] -> "Your Cart (N)"
     .hc-cart__incentive  -> free-shipping state machine + GWP
     .hc-cart__items      -> line items (qty steppers, remove)
     .hc-cart__upsell     -> ONE upgrade card + ONE cross-sell card max
     .hc-cart__foot       -> pinned summary (savings, subtotal, shipping, checkout) */
  var Cart = {
    el: null,
    busy: false,
    jsonHeaders: { "Content-Type": "application/json", "Accept": "application/json" },
    ensure: function () {
      if (this.el) return this.el;
      var d = document.createElement("div");
      d.className = "hc-cart";
      d.innerHTML =
        '<div class="hc-cart__scrim" data-hc-cart-close></div>' +
        '<aside class="hc-cart__panel" role="dialog" aria-label="Your cart" aria-modal="true">' +
          '<div class="hc-cart__head"><div class="hc-cart__head-main"><h3 data-hc-cart-title>Your Cart</h3><p class="hc-cart__head-sub">Egg-safe, naturally - 60-Day Guarantee</p></div><button class="hc-cart__close" data-hc-cart-close aria-label="Close cart">&times;</button></div>' +
          '<div class="hc-cart__scroll">' +
            '<div class="hc-cart__incentive"></div>' +
            '<div class="hc-cart__items"></div>' +
            '<div class="hc-cart__upsell"></div>' +
          '</div>' +
          '<div class="hc-cart__foot"></div>' +
        '</aside>';
      document.body.appendChild(d);
      d.addEventListener("click", function (e) {
        if (e.target.hasAttribute("data-hc-cart-close")) Cart.close();
      });
      /* a11y: ESC closes the drawer (bound once, checks open state) */
      document.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && Cart.el && Cart.el.classList.contains("is-open")) Cart.close();
      });
      this.el = d;
      return d;
    },
    roots: function () {
      var list = [];
      if (this.el) list.push(this.el);
      document.querySelectorAll("[data-hc-cart-root]").forEach(function (r) { list.push(r); });
      return list;
    },
    open: function () {
      var d = this.ensure();
      /* remember the opener so ESC/close can return focus (a11y) */
      this._opener = (document.activeElement && document.activeElement !== document.body) ? document.activeElement : null;
      d.classList.add("is-open");
      document.documentElement.style.overflow = "hidden";
      /* dim stale content until the fresh /cart.js render lands (no outdated
         lines/Checkout tappable during the fetch) */
      this._setStale(true);
      this.refresh();
    },
    close: function () {
      if (this.el) this.el.classList.remove("is-open");
      document.documentElement.style.overflow = "";
      var op = this._opener;
      this._opener = null;
      if (op && document.contains(op) && typeof op.focus === "function") {
        try { op.focus(); } catch (e) {}
      }
    },
    _setStale: function (on) {
      if (!this.el) return;
      [".hc-cart__scroll", ".hc-cart__foot"].forEach(function (sel) {
        var z = Cart.el.querySelector(sel);
        if (!z) return;
        z.style.opacity = on ? "0.5" : "";
        z.style.pointerEvents = on ? "none" : "";
      });
    },
    refresh: function () {
      var self = this;
      fetch("/cart.js", { headers: { "Accept": "application/json" } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { self.render(cart); });
    },
    /* Inspect cart for an upsell product: is it present, and is it a subscription? */
    inspect: function (cart, handle) {
      var found = null;
      (cart.items || []).forEach(function (it) { if (it.handle === handle) found = it; });
      return { inCart: !!found, isSub: !!(found && found.selling_plan_allocation), item: found };
    },
    /* Subscription unit price with Shopify per-unit rounding (2999 -> 2399) */
    unitSub: function (cents) { return Math.round(cents * (1 - CFG.subPct / 100)); },
    /* One-time list price for a cart line (used for the savings line).
       Prefer the allocation's compare price; else reverse the sub discount. */
    listPrice: function (it) {
      var spa = it.selling_plan_allocation;
      if (!spa) return it.final_price;
      if (spa.compare_at_price) return spa.compare_at_price;
      return Math.round(it.final_price / (1 - CFG.subPct / 100));
    },
    anySub: function (cart) {
      return (cart.items || []).some(function (it) { return !!it.selling_plan_allocation; });
    },
    render: function (cart) {
      var self = this;
      this.roots().forEach(function (root) { self.renderRoot(root, cart); });
      this._setStale(false);
      updateCartCount(cart.item_count);
    },
    renderRoot: function (root, cart) {
      var items = root.querySelector(".hc-cart__items");
      if (!items) return;
      var incentive = root.querySelector(".hc-cart__incentive");
      var upsell = root.querySelector(".hc-cart__upsell");
      var foot = root.querySelector(".hc-cart__foot");
      var title = root.querySelector("[data-hc-cart-title]");
      if (title) title.textContent = "Your Cart (" + (cart.item_count || 0) + ")";
      if (!cart.items || !cart.items.length) {
        items.innerHTML = '<div class="hc-cart__empty"><div class="hc-cart__empty-ico">🧺</div><p>Your cart is empty.</p><a class="hc-btn hc-btn--ghost" href="/collections/best-sellers" data-hc-cart-close>Shop best sellers</a></div>';
        if (incentive) incentive.innerHTML = "";
        upsell.innerHTML = "";
        if (foot) foot.innerHTML = "";
        return;
      }
      items.innerHTML = cart.items.map(function (it) {
        var plan = it.selling_plan_allocation ? '<span class="hc-cart__line-sub">Subscription · '+ it.selling_plan_allocation.selling_plan.name +'</span>' : "";
        var opt = it.variant_title && it.variant_title !== "Default Title" ? '<small>'+ it.variant_title +'</small>' : "";
        /* short display name: cut the product title at the em-dash (copy rule: no em-dashes in UI);
           prefer the config short title so line names match the upsell cards */
        var lineTitle = String(it.product_title || "").split("—")[0].trim();
        Object.keys(CartCfg.products || {}).forEach(function (k) {
          var p = CartCfg.products[k];
          if (p && p.handle === it.handle && p.title) lineTitle = p.title;
        });
        return '<div class="hc-cart__line">' +
          '<img src="'+ (it.image ? it.image.replace(/(\.[a-z]+)(\?|$)/i, "_120x120$1$2") : "") +'" alt="" width="64" height="64" loading="lazy">' +
          '<div class="hc-cart__line-main"><b>'+ lineTitle +'</b>'+ opt + plan +
            '<div class="hc-qty hc-qty--sm">' +
              '<button data-hc-qty="-1" data-key="'+ it.key +'" aria-label="Decrease quantity">&minus;</button>' +
              '<input value="'+ it.quantity +'" readonly aria-label="Quantity">' +
              '<button data-hc-qty="1" data-key="'+ it.key +'" aria-label="Increase quantity">+</button>' +
            '</div>' +
          '</div>' +
          '<div class="hc-cart__line-end"><b>'+ money(it.final_line_price) +'</b>' +
            '<button data-hc-remove="'+ it.key +'" class="hc-cart__remove">Remove</button></div>' +
        '</div>';
      }).join("");

      if (incentive) incentive.innerHTML = this.buildIncentive(cart);
      upsell.innerHTML = this.buildUpsell(cart);
      if (foot) foot.innerHTML = this.buildFoot(cart);

      root.querySelectorAll("[data-hc-qty]").forEach(function (b) {
        b.addEventListener("click", function () { Cart.changeByKey(b.dataset.key, parseInt(b.getAttribute("data-hc-qty"), 10), b); });
      });
      root.querySelectorAll("[data-hc-remove]").forEach(function (b) {
        b.addEventListener("click", function (e) {
          e.preventDefault();
          var key = b.getAttribute("data-hc-remove");
          /* cancel any pending qty change for this line - remove wins */
          var p = Cart._pend[key];
          if (p && p.t) { clearTimeout(p.t); p.t = null; }
          delete Cart._pend[key];
          Cart.setByKey(key, 0, b.closest(".hc-cart__line"));
        });
      });
      this.bindUpsell(root);
    },

    /* ---- Incentive zone state machine ----
       sub line       -> free shipping always (subscription perk)
       >= threshold   -> free shipping unlocked
       >= 55% of it   -> "$X.XX from free shipping" + progress bar
       else           -> Subscribe & Save pitch (no demotivating near-empty bar)
       Then the GWP segment (teaser / unlocked card). */
    buildIncentive: function (cart) {
      var total = cart.total_price;
      var T = CFG.threshold;
      var fee = '<p class="hc-cart__feeline">orders under ' + moneyTrim(T) + ' ship for ' + moneyTrim(CFG.shipFee) + '</p>';
      var html;
      if (this.anySub(cart)) {
        html = '<div class="hc-cart__ship is-on">' + this.truck() + '<span><b>Free shipping on your subscription - always.</b></span></div>';
      } else if (total >= T) {
        html = '<div class="hc-cart__ship is-on">' + this.truck() + '<span><b>Free shipping unlocked!</b></span></div>';
      } else if (total >= Math.round(T * 0.55)) {
        var pct = Math.max(20, Math.min(96, Math.round((total / T) * 100)));
        html = '<div class="hc-cart__ship">' + this.truck() + '<span><b>You\'re ' + money(T - total) + ' from free shipping</b></span></div>' +
          '<div class="hc-meter" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="' + pct + '"><span style="width:' + pct + '%"></span></div>' + fee;
      } else {
        html = '<div class="hc-cart__ship">' + this.truck() + '<span><b>Subscribe &amp; Save ' + CFG.subPct + '%</b> - every subscription ships FREE</span></div>' + fee;
      }
      return html + this.buildGwp(cart);
    },

    /* ---- Gift-with-purchase segment ($85 -> free Wound & Skin Spray) ----
       Honest one-tap add; the automatic discount zeroes it at checkout. */
    buildGwp: function (cart) {
      var P = CartCfg.products || {};
      if (!CFG.gwpVariant || !P.woundSpray) return "";
      var total = cart.total_price;
      var ws = this.inspect(cart, P.woundSpray.handle);
      /* the GWP discount targets the single-bottle variant only */
      var wsIsGwpVariant = !!(ws.item && ws.item.variant_id === CFG.gwpVariant);
      if (total >= CFG.gwpThreshold) {
        if (ws.inCart) {
          /* spray already in the cart: confirm when the discount actually zeroed it */
          if (ws.item.final_line_price === 0) {
            return '<div class="hc-gwp is-unlocked"><div class="hc-gwp__body"><b>Your Wound &amp; Skin Spray is FREE</b><span>discount applied (' + money(P.woundSpray.price) + ' value)</span></div></div>';
          }
          return "";
        }
        return '<div class="hc-gwp is-unlocked">' +
          (P.woundSpray.image ? '<img class="hc-gwp__img" src="' + P.woundSpray.image + '" alt="" width="48" height="48" loading="lazy">' : "") +
          '<div class="hc-gwp__body"><b>Free Wound &amp; Skin Spray unlocked</b><span>FREE at checkout - discount applies automatically</span></div>' +
          '<button class="hc-btn hc-gwp__btn" data-hc-gwp-add>Add my free spray</button>' +
        '</div>';
      }
      if (total >= CFG.gwpThreshold - 2000) {
        /* below threshold with the single-bottle spray in the cart (e.g. the gift
           dropped back to paid): say what adding more actually does - no
           "$X to a free spray" teaser while a paid spray sits in the cart */
        if (ws.inCart) {
          if (wsIsGwpVariant && ws.item.final_price > 0) {
            return '<div class="hc-gwp"><div class="hc-gwp__body"><b>' + money(CFG.gwpThreshold - total) + ' more makes your Wound &amp; Skin Spray FREE</b><span>(' + money(P.woundSpray.price) + ' value at ' + money(CFG.gwpThreshold) + '+)</span></div></div>';
          }
          return "";
        }
        return '<div class="hc-gwp"><div class="hc-gwp__body"><b>' + money(CFG.gwpThreshold - total) + ' to a free Wound &amp; Skin Spray</b><span>(' + money(P.woundSpray.price) + ' value)</span></div></div>';
      }
      return "";
    },

    /* ---- Upsell / cross-sell logic ----
       Upgrades: 1 one-time hero line -> single upgrade card;
                 2+ one-time hero lines -> ONE collapsed switch-the-routine card.
       Cross-sell: exactly ONE card by matrix -
         dewormer only -> Golden Yolk; egg only -> Dewormer;
         both powders -> nothing (GWP covers spray); all 3 -> nothing;
         no heroes (spray only) -> gentle Dewormer card, one-time mode. */
    buildUpsell: function (cart) {
      var P = CartCfg.products || {};
      if (!P.dewormer || !P.eggBooster) return "";
      var dw = this.inspect(cart, P.dewormer.handle);
      var eb = this.inspect(cart, P.eggBooster.handle);
      var blocks = [];

      var oneTime = [];
      if (dw.inCart && !dw.isSub) oneTime.push({ p: P.dewormer, item: dw.item });
      if (eb.inCart && !eb.isSub) oneTime.push({ p: P.eggBooster, item: eb.item });
      if (oneTime.length >= 2) blocks.push(this.switchAllCard(oneTime));
      else if (oneTime.length === 1) blocks.push(this.subUpgradeCard(oneTime[0].p, oneTime[0].item));

      var cross = "";
      if (dw.inCart && !eb.inCart) cross = this.crossSellCard(P.eggBooster, "Complete her daily routine", dw.isSub);
      else if (eb.inCart && !dw.inCart) cross = this.crossSellCard(P.dewormer, "Pair it with the daily routine", eb.isSub);
      else if (!dw.inCart && !eb.inCart) cross = this.crossSellCard(P.dewormer, "Keepers add this to every routine", false);
      if (cross) blocks.push(cross);

      if (!blocks.length) return "";
      return '<div class="hc-cart__upsell-head">Make it a routine</div>' + blocks.join("");
    },

    /* True order-level saving from switching a one-time line to a subscription:
       what they pay now (final_line_price, volume discounts included) minus
       what the subscription charges (unitSub of the ORIGINAL unit price x qty
       - the volume discount does not stack with the subscription discount). */
    upgradeSave: function (item) {
      return Math.max(0, item.final_line_price - this.unitSub(item.original_price || item.final_price) * item.quantity);
    },
    subUpgradeCard: function (p, item) {
      var save = this.upgradeSave(item);
      return '<div class="hc-up hc-up--upgrade">' +
        '<div class="hc-up__badge">Save ' + CFG.subPct + '% + free shipping</div>' +
        '<div class="hc-up__row">' +
          (p.image ? '<img class="hc-up__img" src="'+ p.image +'" alt="" width="56" height="56" loading="lazy">' : "") +
          '<div class="hc-up__body">' +
            '<b>Subscribe to your '+ p.title +'</b>' +
            '<span>Switch this to a subscription and save '+ money(save) +' on this order.</span>' +
          '</div>' +
        '</div>' +
        '<button class="hc-btn hc-btn--block hc-up__btn" data-hc-upgrade data-key="'+ item.key +'" data-id="'+ item.variant_id +'" data-qty="'+ item.quantity +'" data-plan="'+ p.sellingPlanId +'">Subscribe &amp; save ' + CFG.subPct + '%</button>' +
        '<p class="hc-up__micro">Skip, pause, or cancel anytime.</p>' +
      '</div>';
    },

    /* One card that switches every one-time hero line to Subscribe & Save */
    switchAllCard: function (list) {
      var self = this;
      var save = 0;
      list.forEach(function (l) { save += self.upgradeSave(l.item); });
      return '<div class="hc-up hc-up--upgrade">' +
        '<div class="hc-up__badge">Save ' + CFG.subPct + '% + free shipping</div>' +
        '<div class="hc-up__row">' +
          '<div class="hc-up__body">' +
            '<b>Switch your routine to Subscribe &amp; Save</b>' +
            '<span>Save ' + money(save) + '/order + free shipping on both.</span>' +
          '</div>' +
        '</div>' +
        '<button class="hc-btn hc-btn--block hc-up__btn" data-hc-switchall>Switch &amp; save ' + money(save) + '/order</button>' +
        '<p class="hc-up__micro">Skip, pause, or cancel anytime.</p>' +
      '</div>';
    },

    /* subMode: match the anchor line - subscription (1-month plan) when the
       anchor is subscribed, plain one-time otherwise. */
    crossSellCard: function (p, line, subMode) {
      var sub = this.unitSub(p.price);
      var priceHtml = subMode
        ? '<em>' + money(sub) + '/mo subscribed</em> <s>' + money(p.price) + '</s>'
        : '<em>' + money(p.price) + '</em> one-time';
      var btn = subMode
        ? '<button class="hc-btn hc-btn--ghost hc-btn--block hc-up__btn" data-hc-crossadd data-id="'+ p.variantId +'" data-plan="'+ p.sellingPlanId +'">Add &amp; subscribe - save ' + CFG.subPct + '%</button>'
        : '<button class="hc-btn hc-btn--ghost hc-btn--block hc-up__btn" data-hc-crossadd data-id="'+ p.variantId +'">Add to cart - ' + money(p.price) + '</button>';
      return '<div class="hc-up hc-up--cross">' +
        '<div class="hc-up__row">' +
          (p.image ? '<img class="hc-up__img" src="'+ p.image +'" alt="" width="56" height="56" loading="lazy">' : "") +
          '<div class="hc-up__body">' +
            '<b>'+ p.title +'</b>' +
            '<span>'+ line +'. '+ priceHtml +'</span>' +
          '</div>' +
        '</div>' + btn +
      '</div>';
    },

    /* ---- Pinned summary ---- */
    buildFoot: function (cart) {
      var self = this;
      var savings = 0;
      (cart.items || []).forEach(function (it) {
        if (!it.selling_plan_allocation) return;
        savings += Math.max(0, self.listPrice(it) - it.final_price) * it.quantity;
      });
      var free = this.anySub(cart) || cart.total_price >= CFG.threshold;
      return (savings > 0 ? '<div class="hc-cart__save">You\'re saving ' + money(savings) + '</div>' : '') +
        '<div class="hc-cart__total"><span>Subtotal</span><span>' + money(cart.total_price) + '</span></div>' +
        '<div class="hc-cart__shiprow"><span>Shipping</span><span>' + (free ? '<b class="hc-cart__shipfree">FREE</b>' : 'calculated at checkout') + '</span></div>' +
        '<a class="hc-btn hc-btn--block hc-btn--lg" href="/checkout">Checkout</a>' +
        '<p class="hc-cart__foot-note">60-Day Happy Flock Guarantee · Skip, pause, or cancel anytime</p>';
    },

    truck: function () {
      return '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M1 3h15v13H1z"/><path d="M16 8h4l3 3v5h-7z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>';
    },

    /* ---- Hardened one-time -> subscription swap ----
       Checks the change.js response; if the re-add fails, restores the
       original one-time line before rejecting. No silent cart-line loss. */
    swapLine: function (key, id, qty, plan) {
      return fetch("/cart/change.js", { method: "POST", headers: Cart.jsonHeaders, body: JSON.stringify({ id: key, quantity: 0 }) })
        .then(function (r) {
          if (!r.ok) return Promise.reject({ stage: "change", restored: true });
          return r.json();
        })
        .then(function () {
          return fetch("/cart/add.js", { method: "POST", headers: Cart.jsonHeaders, body: JSON.stringify({ items: [{ id: id, quantity: qty, selling_plan: plan }] }) });
        })
        .then(function (r) {
          if (!r.ok) {
            return fetch("/cart/add.js", { method: "POST", headers: Cart.jsonHeaders, body: JSON.stringify({ items: [{ id: id, quantity: qty }] }) })
              .then(function (r2) { return Promise.reject({ stage: "add", restored: r2.ok }); },
                    function () { return Promise.reject({ stage: "add", restored: false }); });
          }
          return r.json();
        });
    },

    /* Revert an already-upgraded line back to one-time (rollback path) */
    revertSwap: function (id, qty, plan) {
      return fetch("/cart.js", { headers: { "Accept": "application/json" } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var line = null;
          (cart.items || []).forEach(function (it) {
            if (it.variant_id === id && it.selling_plan_allocation && it.selling_plan_allocation.selling_plan.id === plan) line = it;
          });
          if (!line) return;
          return fetch("/cart/change.js", { method: "POST", headers: Cart.jsonHeaders, body: JSON.stringify({ id: line.key, quantity: 0 }) })
            .then(function () {
              return fetch("/cart/add.js", { method: "POST", headers: Cart.jsonHeaders, body: JSON.stringify({ items: [{ id: id, quantity: qty }] }) });
            });
        });
    },

    bindUpsell: function (root) {
      var d = root || this.el; if (!d) return;
      // Single one-time -> subscription upgrade (hardened)
      d.querySelectorAll("[data-hc-upgrade]").forEach(function (b) {
        b.addEventListener("click", function () {
          if (Cart.busy) return; Cart.busy = true;
          var key = b.getAttribute("data-key");
          var id = parseInt(b.getAttribute("data-id"), 10);
          var qty = parseInt(b.getAttribute("data-qty"), 10) || 1;
          var plan = parseInt(b.getAttribute("data-plan"), 10);
          var lbl = b.innerHTML; b.textContent = "Upgrading…"; b.disabled = true;
          Cart.swapLine(key, id, qty, plan)
            .then(function () { Cart.busy = false; Cart.refresh(); toast("Subscription added - " + CFG.subPct + "% saved"); })
            .catch(function (err) {
              Cart.busy = false; b.innerHTML = lbl; b.disabled = false; Cart.refresh();
              toast(err && err.restored === false ? "Something went wrong - please check your cart" : "Couldn't upgrade - your item is still in the cart", true);
            });
        });
      });
      // Collapsed card: switch EVERY one-time hero line, sequential with rollback
      d.querySelectorAll("[data-hc-switchall]").forEach(function (b) {
        b.addEventListener("click", function () {
          if (Cart.busy) return; Cart.busy = true;
          var lbl = b.innerHTML; b.textContent = "Switching…"; b.disabled = true;
          var P = CartCfg.products || {};
          fetch("/cart.js", { headers: { "Accept": "application/json" } })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              var lines = [];
              (cart.items || []).forEach(function (it) {
                if (it.selling_plan_allocation) return;
                if (P.dewormer && it.handle === P.dewormer.handle) lines.push({ key: it.key, id: it.variant_id, qty: it.quantity, plan: P.dewormer.sellingPlanId });
                if (P.eggBooster && it.handle === P.eggBooster.handle) lines.push({ key: it.key, id: it.variant_id, qty: it.quantity, plan: P.eggBooster.sellingPlanId });
              });
              if (!lines.length) return Promise.reject({ restored: true });
              var done = [];
              var chain = Promise.resolve();
              lines.forEach(function (ln) {
                chain = chain.then(function () {
                  return Cart.swapLine(ln.key, ln.id, ln.qty, ln.plan).then(function () { done.push(ln); });
                });
              });
              return chain.catch(function (err) {
                // roll back any lines already upgraded so the cart matches what the customer had
                var rb = Promise.resolve();
                done.forEach(function (ln) { rb = rb.then(function () { return Cart.revertSwap(ln.id, ln.qty, ln.plan); }); });
                return rb.then(function () { return Promise.reject(err); }, function () { return Promise.reject(err); });
              });
            })
            .then(function () { Cart.busy = false; Cart.refresh(); toast("Routine switched - " + CFG.subPct + "% saved on every delivery"); })
            .catch(function (err) {
              Cart.busy = false; b.innerHTML = lbl; b.disabled = false; Cart.refresh();
              toast(err && err.restored === false ? "Something went wrong - please check your cart" : "Couldn't switch - your items are still in the cart", true);
            });
        });
      });
      // Cross-sell: add complement matching the anchor's mode (sub plan optional)
      d.querySelectorAll("[data-hc-crossadd]").forEach(function (b) {
        b.addEventListener("click", function () {
          if (Cart.busy) return; Cart.busy = true;
          var id = parseInt(b.getAttribute("data-id"), 10);
          var plan = parseInt(b.getAttribute("data-plan"), 10);
          var item = { id: id, quantity: 1 };
          if (plan) item.selling_plan = plan;
          var lbl = b.innerHTML; b.textContent = "Adding…"; b.disabled = true;
          fetch("/cart/add.js", { method: "POST", headers: Cart.jsonHeaders, body: JSON.stringify({ items: [item] }) })
            .then(function (r) { if (!r.ok) return Promise.reject(); return r.json(); })
            .then(function () { Cart.busy = false; Cart.refresh(); toast(plan ? "Added to your routine - " + CFG.subPct + "% saved" : "Added to your cart"); })
            .catch(function () { Cart.busy = false; b.innerHTML = lbl; b.disabled = false; toast("Couldn't add - please try again", true); });
        });
      });
      // GWP: one-tap add the free spray (one-time; auto discount zeroes it at checkout)
      d.querySelectorAll("[data-hc-gwp-add]").forEach(function (b) {
        b.addEventListener("click", function () {
          if (Cart.busy) return; Cart.busy = true;
          var lbl = b.innerHTML; b.textContent = "Adding…"; b.disabled = true;
          fetch("/cart/add.js", { method: "POST", headers: Cart.jsonHeaders, body: JSON.stringify({ items: [{ id: CFG.gwpVariant, quantity: 1 }] }) })
            .then(function (r) { if (!r.ok) return Promise.reject(); return r.json(); })
            .then(function () { Cart.busy = false; Cart.refresh(); toast("Free spray added - discount applies at checkout"); })
            .catch(function () { Cart.busy = false; b.innerHTML = lbl; b.disabled = false; toast("Couldn't add - please try again", true); });
        });
      });
    },
    /* ---- Qty steppers: instant feedback + coalesced network calls ----
       Rapid +/- clicks update the visible number immediately and are
       debounced (~250ms) into ONE change.js call with the final qty.
       While a request is in flight the line dims and its buttons disable.
       A sequence counter drops stale responses (e.g. a change resolving
       after a remove) so the drawer never renders an outdated cart. */
    _pend: {},
    _seq: 0,
    changeByKey: function (key, delta, btn) {
      var line = btn && btn.closest ? btn.closest(".hc-cart__line") : null;
      var input = line ? line.querySelector(".hc-qty input") : null;
      var p = this._pend[key] || (this._pend[key] = { qty: null, t: null });
      var cur = (typeof p.qty === "number") ? p.qty : (input ? parseInt(input.value, 10) : NaN);
      if (isNaN(cur)) {
        /* no DOM context (shouldn't happen) - fall back to a lookup */
        fetch("/cart.js").then(function(r){return r.json();}).then(function(cart){
          var it = (cart.items || []).filter(function(i){return i.key===key;})[0];
          if (it) Cart.setByKey(key, Math.max(0, it.quantity + delta), line);
        });
        return;
      }
      p.qty = Math.max(0, cur + delta);
      if (input) input.value = p.qty;
      if (line) line.style.opacity = "0.55"; /* immediate busy feedback */
      clearTimeout(p.t);
      p.t = setTimeout(function () {
        p.t = null;
        Cart.setByKey(key, p.qty, line);
      }, 250);
    },
    setByKey: function (key, qty, line) {
      if (line) {
        line.style.opacity = "0.55";
        line.querySelectorAll("button").forEach(function (b) { b.disabled = true; });
      }
      var seq = ++Cart._seq;
      fetch("/cart/change.js", { method: "POST", headers: this.jsonHeaders, body: JSON.stringify({ id: key, quantity: qty }) })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          delete Cart._pend[key];
          if (seq === Cart._seq) Cart.render(cart); /* stale responses are skipped; the newest one renders */
        })
        .catch(function () {
          delete Cart._pend[key];
          if (seq === Cart._seq) Cart.refresh(); /* resync on error */
        });
    },
    /* Busy state that PRESERVES the button's original child nodes.
       PDP buy boxes cache refs to <span data-hc-ctatext>/<span data-b2-ctatext>
       inside the CTA; setting textContent destroyed those spans and froze the
       price label after the first add. Instead we detach the original nodes,
       show a temporary busy span, and re-attach the SAME nodes afterwards
       (any price updates written to them while detached show up on restore). */
    setBtnBusy: function (btn, on) {
      if (!btn) return;
      if (on) {
        if (btn._hcBusy) return;
        btn._hcBusy = true;
        btn._hcKids = Array.prototype.slice.call(btn.childNodes);
        btn._hcKids.forEach(function (n) { btn.removeChild(n); });
        var s = document.createElement("span");
        s.textContent = "Adding…";
        btn._hcBusySpan = s;
        btn.appendChild(s);
        btn.setAttribute("aria-busy", "true");
        btn.disabled = true;
      } else {
        if (!btn._hcBusy) return;
        btn._hcBusy = false;
        if (btn._hcBusySpan && btn._hcBusySpan.parentNode === btn) btn.removeChild(btn._hcBusySpan);
        btn._hcBusySpan = null;
        (btn._hcKids || []).forEach(function (n) { btn.appendChild(n); });
        btn._hcKids = null;
        btn.removeAttribute("aria-busy");
        btn.disabled = false;
      }
    },
    add: function (payload, btn) {
      if (btn && btn._hcBusy) return Promise.resolve(); /* rapid double-click guard */
      this.setBtnBusy(btn, true);
      return fetch("/cart/add.js", { method: "POST", headers: this.jsonHeaders, body: JSON.stringify(payload) })
        .then(function (r) { return r.json().then(function(j){ return { ok: r.ok, body: j }; }); })
        .then(function (res) {
          Cart.setBtnBusy(btn, false);
          if (!res.ok) { toast((res.body && res.body.description) || "Couldn't add to cart", true); return; }
          fetch("/cart.js").then(function(r){return r.json();}).then(function(cart){
            // On the /cart page re-render in place; everywhere else open the drawer
            if (document.querySelector("[data-hc-cart-root]")) { Cart.render(cart); toast("Added to your cart"); }
            else { updateCartCount(cart.item_count); Cart.open(); }
          });
        })
        .catch(function () { Cart.setBtnBusy(btn, false); toast("Network error - please try again", true); });
    }
  };

  function updateCartCount(n) {
    document.querySelectorAll(".cart-count, [data-cart-count], .cart-count-bubble span:first-child").forEach(function (el) { el.textContent = n; });
    // Horizon cart bubble
    document.querySelectorAll("cart-icon, .header__cart").forEach(function(el){ el.setAttribute("data-count", n); });
  }

  /* ---------- Toast ---------- */
  var toastEl;
  function toast(msg, isErr) {
    if (!toastEl) { toastEl = document.createElement("div"); toastEl.className = "hc-toast"; document.body.appendChild(toastEl); }
    toastEl.innerHTML = (isErr ? "" : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>') + "<span>" + msg + "</span>";
    toastEl.classList.add("is-on");
    clearTimeout(toastEl._t);
    toastEl._t = setTimeout(function () { toastEl.classList.remove("is-on"); }, 2600);
  }

  /* ---------- Global add-to-cart (cards / quick add) ---------- */
  function initQuickAdd() {
    document.addEventListener("click", function (e) {
      var btn = e.target.closest("[data-hc-add]");
      if (!btn) return;
      e.preventDefault();
      var id = btn.getAttribute("data-hc-add");
      if (!id) return;
      var item = { id: parseInt(id, 10), quantity: 1 };
      // optional selling plan: read the chosen plan radio in the card, else the button's own plan
      var card = btn.closest("[data-hc-sgcard]");
      var planAttr = btn.getAttribute("data-hc-plan");
      if (card) {
        var chosen = card.querySelector("[data-hc-sgplan]:checked");
        if (chosen && chosen.value === "subscribe" && planAttr) { item.selling_plan = parseInt(planAttr, 10); }
      } else if (planAttr) {
        item.selling_plan = parseInt(planAttr, 10);
      }
      Cart.add({ items: [item] }, btn);
    });
    // per-card plan toggle visuals + button label
    document.addEventListener("change", function (e) {
      var radio = e.target.closest("[data-hc-sgplan]");
      if (!radio) return;
      var card = radio.closest("[data-hc-sgcard]");
      if (!card) return;
      card.querySelectorAll(".hc-sg__plan").forEach(function (l) {
        var input = l.querySelector("[data-hc-sgplan]");
        l.classList.toggle("is-on", input && input.checked);
      });
      var addBtn = card.querySelector(".hc-sg__add");
      if (addBtn) {
        var sub = card.querySelector('[data-hc-sgplan][value="subscribe"]');
        addBtn.textContent = (radio.value === "subscribe" && sub) ? "Subscribe & Save" : "Add to basket";
      }
    });
    // bundle: one-tap add the whole routine (subscription where available)
    document.addEventListener("click", function (e) {
      var btn = e.target.closest("[data-hc-bundle-add]");
      if (!btn) return;
      e.preventDefault();
      var section = btn.closest(".hc-bundle");
      var json = section && section.querySelector("[data-hc-bundle-items]");
      if (!json) return;
      var items;
      try { items = JSON.parse(json.textContent); } catch (err) { return; }
      if (!items || !items.length) return;
      Cart.add({ items: items }, btn);
    });
    document.addEventListener("click", function (e) {
      if (e.target.closest("[data-hc-open-cart]")) { e.preventDefault(); Cart.open(); }
    });
  }

  /* ---------- PDP form ---------- */
  function initPDP() {
    var form = document.querySelector("[data-hc-pdp]");
    if (!form) return;
    var data;
    try { data = JSON.parse(document.getElementById(form.getAttribute("data-variants")).textContent); }
    catch (e) { return; }
    var state = { variant: data.variants.filter(function(v){return v.available;})[0] || data.variants[0], plan: form.getAttribute("data-default-plan") === "subscribe" ? "subscribe" : "onetime", qty: 1 };
    var subPct = parseFloat(form.getAttribute("data-sub-pct")) || 15;

    var priceNow = form.querySelector("[data-hc-price-now]");
    var priceWas = form.querySelector("[data-hc-price-was]");
    var pillEl = form.querySelector("[data-hc-price-pill]");
    var subPriceEls = form.querySelectorAll("[data-hc-sub-price]");
    var otPriceEls = form.querySelectorAll("[data-hc-ot-price]");
    var addBtn = form.querySelector("[data-hc-addbtn]");

    function render() {
      var v = state.variant;
      var base = v.price;
      var sub = Math.round(base * (1 - subPct / 100));
      otPriceEls.forEach(function(el){ el.textContent = money(base); });
      subPriceEls.forEach(function(el){ el.textContent = money(sub); });
      var shown = state.plan === "subscribe" ? sub : base;
      if (priceNow) priceNow.textContent = money(shown);
      if (priceWas) {
        if (state.plan === "subscribe") { priceWas.textContent = money(base); priceWas.style.display = ""; }
        else if (v.compare_at_price && v.compare_at_price > base) { priceWas.textContent = money(v.compare_at_price); priceWas.style.display = ""; }
        else { priceWas.style.display = "none"; }
      }
      if (pillEl) {
        if (state.plan === "subscribe") { pillEl.textContent = "Save " + subPct + "%"; pillEl.style.display = ""; }
        else if (v.compare_at_price && v.compare_at_price > base) { pillEl.textContent = "Save " + money(v.compare_at_price - base); pillEl.style.display = ""; }
        else { pillEl.style.display = "none"; }
      }
      if (addBtn) addBtn.textContent = state.plan === "subscribe" ? "Subscribe & save" : "Add to basket - " + money(shown);
      // sticky bar mirror
      var sbPrice = document.querySelector("[data-hc-sb-price]");
      if (sbPrice) sbPrice.textContent = money(shown);
    }

    // size radios
    form.querySelectorAll("[data-hc-variant]").forEach(function (input) {
      input.addEventListener("change", function () {
        var v = data.variants.filter(function (x) { return String(x.id) === input.value; })[0];
        if (v) { state.variant = v; render(); swapImage(v); }
      });
    });
    // purchase type
    form.querySelectorAll("[data-hc-plan]").forEach(function (input) {
      input.addEventListener("change", function () {
        state.plan = input.value;
        form.querySelectorAll(".hc-purchase__opt").forEach(function (o) { o.classList.toggle("is-on", o.contains(input) && input.checked); });
        render();
      });
    });
    // qty
    var qtyInput = form.querySelector("[data-hc-qty-input]");
    form.querySelectorAll("[data-hc-qty-btn]").forEach(function (b) {
      b.addEventListener("click", function () {
        state.qty = Math.max(1, state.qty + parseInt(b.getAttribute("data-hc-qty-btn"), 10));
        if (qtyInput) qtyInput.value = state.qty;
      });
    });

    function swapImage(v) {
      if (!v.featured_image) return;
      var main = document.querySelector("[data-hc-main-img] img");
      if (main) main.src = v.featured_image;
    }
    // thumbnails
    document.querySelectorAll("[data-hc-thumb]").forEach(function (t) {
      t.addEventListener("click", function () {
        var main = document.querySelector("[data-hc-main-img] img");
        if (main) main.src = t.getAttribute("data-hc-thumb");
        document.querySelectorAll("[data-hc-thumb]").forEach(function (x) { x.classList.remove("is-active"); });
        t.classList.add("is-active");
      });
    });

    function doAdd(btn) {
      var item = { id: parseInt(state.variant.id, 10), quantity: state.qty };
      var plan = state.variant.selling_plan_id || form.getAttribute("data-sub-plan-id");
      if (state.plan === "subscribe" && plan) item.selling_plan = parseInt(plan, 10);
      Cart.add({ items: [item] }, btn);
    }
    if (addBtn) addBtn.addEventListener("click", function (e) { e.preventDefault(); doAdd(addBtn); });
    var sbBtn = document.querySelector("[data-hc-sb-add]");
    if (sbBtn) sbBtn.addEventListener("click", function (e) { e.preventDefault(); doAdd(sbBtn); });

    // sticky bar visibility
    var bar = document.querySelector(".hc-stickybar");
    if (bar && addBtn && "IntersectionObserver" in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { bar.classList.toggle("is-on", !en.isIntersecting); });
      }, { rootMargin: "-80px 0px 0px 0px" });
      io.observe(addBtn);
    }
    render();
  }

  /* ---------- Branded /cart page: hydrate the server-rendered cart ---------- */
  function initCartPage() {
    if (document.querySelector("[data-hc-cart-root]")) Cart.refresh();
  }

  function boot() {
    initReveals(); initParallax(); initVideo(); initMarquee(); initQuickAdd(); initPDP(); initCartPage();
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
  // Re-init in Shopify theme editor
  document.addEventListener("shopify:section:load", boot);
  window.HCcart = Cart;
})();
